import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth-context';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ArrowRight, LineChart, TrendingUp, HardHat } from 'lucide-react';

const LOGIN_HERO_IMG = '/login-hero.png';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const loggedUser = await login(email, password);
      navigate(loggedUser?.role === 'super_admin' ? '/admin' : '/');
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al iniciar sesión');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" style={{ background: '#F8FAFC' }}>
      {/* Left — Form */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 sm:px-12 py-12">
        <div className="w-full max-w-[400px]">
          {/* Logo */}
          <div className="flex justify-center mb-12">
            <img src="/assets/stratika-logo.png" alt="Stratika" className="h-12" style={{ objectFit: 'contain' }} />
          </div>

          {/* Form */}
          <div>
            <h2 className="font-display text-2xl font-semibold mb-1" style={{ color: '#0F172A' }}>Iniciar sesión</h2>
            <p className="text-sm mb-7" style={{ color: '#64748B' }}>Ingresa a tu plataforma de inteligencia inmobiliaria</p>

            <form onSubmit={handleSubmit} className="space-y-5">
              {error && (
                <div className="rounded-lg px-4 py-3 text-sm" style={{ background: 'var(--danger-soft)', color: 'var(--danger)' }} data-testid="login-error">
                  {error}
                </div>
              )}
              <div className="space-y-1.5">
                <Label htmlFor="email" className="text-sm font-medium" style={{ color: '#0F172A' }}>Correo electrónico</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="tu@empresa.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="h-11"
                  style={{ borderColor: '#E2E8F0', background: '#FFFFFF' }}
                  data-testid="login-email-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="password" className="text-sm font-medium" style={{ color: '#0F172A' }}>Contraseña</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="h-11"
                  style={{ borderColor: '#E2E8F0', background: '#FFFFFF' }}
                  data-testid="login-password-input"
                />
              </div>
              <Button
                type="submit"
                className="w-full h-11 text-sm font-semibold text-white"
                disabled={loading}
                style={{ background: '#6366F1', borderColor: 'transparent' }}
                onMouseOver={(e) => e.currentTarget.style.background = '#5558E3'}
                onMouseOut={(e) => e.currentTarget.style.background = '#6366F1'}
                data-testid="login-submit-button"
              >
                {loading ? 'Iniciando...' : 'Iniciar sesión'}
                {!loading && <ArrowRight className="ml-2 h-4 w-4" />}
              </Button>
            </form>
            <p className="mt-8 text-center text-sm" style={{ color: '#94A3B8' }}>
              ¿No tienes cuenta?{' '}
              <Link to="/register" className="font-semibold transition-colors" style={{ color: '#6366F1' }} data-testid="goto-register-link">
                Regístrate
              </Link>
            </p>
            <a href="https://www.swaraya.ai" target="_blank" rel="noopener noreferrer" className="mt-10 inline-flex items-center justify-center gap-1.5 opacity-80 hover:opacity-100 transition-opacity w-full">
              <span className="text-[9px] uppercase tracking-[0.14em]" style={{ color: '#94A3B8' }}>Powered by</span>
              <img src="/swaraya-wordmark.png" alt="Swaraya" className="h-4" />
            </a>
          </div>
        </div>
      </div>

      {/* Right — Hero Image */}
      <div className="hidden lg:block lg:w-[50%] relative overflow-hidden">
        <img src={LOGIN_HERO_IMG} alt="Desarrollo inmobiliario moderno" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(15,23,42,0.90) 0%, rgba(15,23,42,0.45) 50%, rgba(15,23,42,0.20) 100%)' }} />
        <div className="relative z-10 flex flex-col justify-end h-full p-10 pb-14">
          <h2 className="text-3xl font-display font-semibold text-white mb-3 leading-tight">
            Inteligencia financiera<br />para desarrolladores
          </h2>
          <p className="text-white/50 text-base mb-8 max-w-md">
            Análisis riguroso. Claridad ejecutiva. Decisiones precisas.
          </p>
          <div className="flex gap-6">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'rgba(99,102,241,0.15)' }}>
                <LineChart className="w-4 h-4" style={{ color: '#A5B4FC' }} />
              </div>
              <span className="text-white/60 text-xs font-medium">Financial<br/>Intelligence</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'rgba(13,189,139,0.15)' }}>
                <TrendingUp className="w-4 h-4" style={{ color: '#6EE7B7' }} />
              </div>
              <span className="text-white/60 text-xs font-medium">Market<br/>Intelligence</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'rgba(245,158,11,0.15)' }}>
                <HardHat className="w-4 h-4" style={{ color: '#FCD34D' }} />
              </div>
              <span className="text-white/60 text-xs font-medium">Construction<br/>Intelligence</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
